import { Badge } from "@/components/ui/badge.js";
import { cn } from "@/lib/cn.js";
import { durationMinutes, isoDate } from "@/lib/format.js";
import type { Appointment, Customer, Resource, Service, SyncConflict, TenantClosure } from "@/lib/types.js";
import { AppointmentBlock } from "./appointment-block.js";
import { DAY_START_HOUR, MINUTES_PER_ROW, ROW_HEIGHT_PX, TOTAL_ROWS } from "./constants.js";
import { pickDayClosure, tenantDayLabel } from "./closures-overlay.js";

/**
 * Resource view (PRD calendar-ux-parity REQ-CUX-005): a single column for
 * the selected resource, full day vertical timeline (07:00–20:00). Same
 * data as day-view but single-column. The principal use case is "see one
 * stylist's day at a glance without the parallel-resource columns".
 */
export function CalendarResourceGrid({
  day,
  tenantTz,
  resource,
  appointments,
  services,
  customers,
  conflicts,
  closures,
  onResize,
  onClickEmpty,
  onClickAppointment
}: {
  day: Date;
  tenantTz: string;
  resource: Resource;
  appointments: Appointment[];
  services: Service[];
  customers: Customer[];
  conflicts: SyncConflict[];
  closures: TenantClosure[];
  onResize: (appointmentId: string, newEndsAt: string) => Promise<void>;
  onClickEmpty: (startsAt: string, resourceId: string) => void;
  onClickAppointment: (a: Appointment) => void;
}): React.JSX.Element {
  const serviceById = new Map(services.map((s) => [s.id, s]));
  const customerById = new Map(customers.map((c) => [c.id, c]));
  const rows = Array.from({ length: TOTAL_ROWS }, (_, i) => i);

  const dayStart = new Date(day);
  dayStart.setHours(DAY_START_HOUR, 0, 0, 0);
  const iso = isoDate(day);
  const dayLabel = tenantDayLabel(day, tenantTz);
  const dayClosure = pickDayClosure(closures, dayLabel, tenantTz);

  function isoForRow(row: number): string {
    const d = new Date(dayStart);
    d.setMinutes(d.getMinutes() + row * MINUTES_PER_ROW);
    return d.toISOString();
  }

  const sameResource = appointments.filter(
    (a) => a.primary_resource_id === resource.id && tenantDayLabel(a.starts_at, tenantTz) === dayLabel
  );

  return (
    <div data-testid="calendar-resource-grid" className="overflow-hidden rounded-lg border bg-card shadow-xs">
      <div className="overflow-auto scrollbar-thin">
        <div className="grid" style={{ gridTemplateColumns: `56px minmax(360px, 1fr)` }}>
          <div className="sticky top-0 z-10 border-b bg-card/90 backdrop-blur" />
          <div className="sticky top-0 z-10 flex items-center justify-between gap-2 border-b border-l bg-card/90 px-3 py-2.5 backdrop-blur">
            <span className="truncate text-sm font-semibold text-foreground">{resource.name}</span>
            {resource.freshness_state !== "fresh" && (
              <Badge variant="warning" className="capitalize">
                {resource.freshness_state}
              </Badge>
            )}
          </div>

          {/* Time gutter */}
          <div className="relative border-r">
            {rows.map((row) => {
              const minutes = row * MINUTES_PER_ROW;
              const showLabel = minutes % 60 === 0;
              return (
                <div
                  key={row}
                  style={{ height: ROW_HEIGHT_PX }}
                  className={cn("relative", showLabel && "border-t")}
                >
                  {showLabel && (
                    <span className="absolute -top-2 right-2 bg-card px-1 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                      {formatHourLabel(DAY_START_HOUR + Math.floor(minutes / 60))}
                    </span>
                  )}
                </div>
              );
            })}
          </div>

          {/* The single resource column */}
          <div
            data-testid={`resource-day-col-${iso}`}
            data-closed={dayClosure ? "true" : "false"}
            className={cn("relative border-l bg-background/40", dayClosure && "bg-muted/40")}
            style={{ height: TOTAL_ROWS * ROW_HEIGHT_PX }}
          >
            {dayClosure && (
              <>
                <div
                  aria-hidden
                  className="pointer-events-none absolute inset-0 z-[1]"
                  style={{
                    backgroundImage:
                      "repeating-linear-gradient(45deg, rgba(160, 160, 160, 0.18) 0, rgba(160, 160, 160, 0.18) 6px, transparent 6px, transparent 12px)"
                  }}
                />
                <span
                  data-testid={`resource-day-closure-chip-${iso}`}
                  className="pointer-events-none absolute left-1.5 top-1.5 z-[2] inline-flex items-center gap-1 rounded-md bg-warning/30 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-warning-foreground"
                >
                  Closed · {dayClosure.reason}
                </span>
              </>
            )}
            {Array.from({ length: TOTAL_ROWS }, (_, i) => (
              <div
                key={i}
                className={cn(
                  "absolute left-0 right-0 cursor-pointer transition-colors duration-100 hover:bg-primary/5 motion-reduce:transition-none",
                  i % 4 === 0 && "border-t border-border",
                  i % 4 === 2 && "border-t border-border/40"
                )}
                style={{ top: i * ROW_HEIGHT_PX, height: ROW_HEIGHT_PX }}
                onClick={() => onClickEmpty(isoForRow(i), resource.id)}
              />
            ))}
            {sameResource.map((appointment) => {
              const top = minutesFromDayStart(appointment.starts_at, dayStart) / MINUTES_PER_ROW;
              const height = Math.max(2, durationMinutes(appointment.starts_at, appointment.ends_at) / MINUTES_PER_ROW);
              if (top < 0 || top > TOTAL_ROWS) return null;
              const service = serviceById.get(appointment.service_id);
              const customer = customerById.get(appointment.customer_id);
              return (
                <AppointmentBlock
                  key={appointment.id}
                  top={top * ROW_HEIGHT_PX}
                  height={height * ROW_HEIGHT_PX}
                  appointment={appointment}
                  service={service}
                  customer={customer}
                  conflicts={conflicts}
                  resizable
                  onClick={() => onClickAppointment(appointment)}
                  onResize={onResize}
                />
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function minutesFromDayStart(iso: string, dayStart: Date): number {
  return Math.floor((Date.parse(iso) - dayStart.getTime()) / 60000);
}

function formatHourLabel(hour: number): string {
  const am = hour < 12 || hour === 24;
  const display = hour % 12 === 0 ? 12 : hour % 12;
  return `${display}${am ? "a" : "p"}`;
}
